import { useState } from 'react';
import { Link } from 'react-router';
import { Button, Card, Skeleton, cn, useToast } from '@mgdlead/ui';
import { can } from '@mgdlead/shared';
import { ArrowRight, TrendingDown } from 'lucide-react';

import { useWorkspace } from '../workspace/WorkspaceContext';
import { workspacePath } from '../workspace/navigation';
import {
  useDailyRecommendations,
  useDismissRecommendation,
  type DailyRecommendation,
} from '../../lib/api';
import { humanizeError } from '../../lib/errors';

type Motivo = 'JA_RESOLVI' | 'NAO_FAZ_SENTIDO' | null;

/** As três saídas do "Dispensar" — botões do @mgdlead/ui (foco visível, carregando, toque). */
const MOTIVOS: ReadonlyArray<{ motivo: Motivo; rotulo: string }> = [
  { motivo: 'JA_RESOLVI', rotulo: 'Já resolvi' },
  { motivo: 'NAO_FAZ_SENTIDO', rotulo: 'Não faz sentido' },
  { motivo: null, rotulo: 'Só dispensar' },
];

/**
 * G4 — as até 3 recomendações do dia, no bloco "O que fazer hoje" do Dashboard.
 *
 * Cada item: o FATO (com o número), a CONSEQUÊNCIA, a contagem que sustenta e UM botão que
 * leva à tela — nunca uma ação executada (invariante 13: sugerir não é decidir). Tudo vem
 * pronto do servidor; aqui só se desenha.
 */

type Destino = DailyRecommendation['acao']['destino'];

export function hrefDoDestino(slug: string, d: Destino): string {
  switch (d.kind) {
    case 'CAMPANHA':
      return `${workspacePath(slug, `campanhas/${d.campaignId}`)}${d.aba === 'retencao' ? '#retencao' : ''}`;
    case 'GESTAO':
      return workspacePath(slug, 'analytics/gestao');
    case 'METAS':
      return `${workspacePath(slug, 'configuracoes')}?aba=metas`;
  }
}

export type RecomendacoesState =
  | { estado: 'carregando' }
  | { estado: 'erro'; mensagem: string; tentar: () => void }
  | { estado: 'pronto'; itens: DailyRecommendation[] };

/** A consulta, separada do desenho para o bloco decidir o vazio junto com os outros avisos. */
export function useRecomendacoes(): RecomendacoesState {
  const { workspace } = useWorkspace();
  const q = useDailyRecommendations(workspace.id);
  if (q.isPending) return { estado: 'carregando' };
  if (q.isError || !q.data) {
    return { estado: 'erro', mensagem: humanizeError(q.error), tentar: () => void q.refetch() };
  }
  return { estado: 'pronto', itens: q.data.itens };
}

export function RecomendacoesSkeleton() {
  return <Skeleton className="h-36 w-full rounded-lg" />;
}

export function RecomendacoesErro({ tentar }: { tentar: () => void }) {
  return (
    <Card padded={false} className="flex flex-col gap-2 p-4 text-sm">
      <p className="text-foreground">Não conseguimos montar as recomendações de hoje.</p>
      <div>
        <Button size="sm" variant="secondary" onClick={tentar}>
          Tentar de novo
        </Button>
      </div>
    </Card>
  );
}

export function RecomendacaoCard({ item }: { item: DailyRecommendation }) {
  const { workspace, role } = useWorkspace();
  const dismiss = useDismissRecommendation();
  const { toast } = useToast();
  const [perguntando, setPerguntando] = useState(false);
  const [escolhido, setEscolhido] = useState<Motivo | undefined>(undefined);
  const podeDispensar = can(role, 'incident:acknowledge');

  const dispensar = (motivo: Motivo) => {
    dismiss.mutate(
      { workspaceId: workspace.id, chave: item.chave, motivo },
      {
        onSuccess: () => toast({ title: 'Recomendação dispensada por hoje', tone: 'success' }),
        onError: (e) =>
          toast({ title: 'Não deu para dispensar', description: humanizeError(e), tone: 'danger' }),
      },
    );
  };

  return (
    <Card padded={false} className="flex flex-col p-4">
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'inline-flex size-9 shrink-0 items-center justify-center rounded-md',
            item.severidade === 'VERMELHO'
              ? 'bg-danger-soft text-danger'
              : 'bg-warning-soft text-warning',
          )}
        >
          <TrendingDown aria-hidden="true" className="size-4" />
          <span className="sr-only">
            {item.severidade === 'VERMELHO' ? 'Fora da meta' : 'Atenção'}
          </span>
        </span>
        <div className="min-w-0">
          <p className="font-medium text-foreground">{item.fato}</p>
          <p className="mt-1 text-sm text-muted-foreground">{item.consequencia}</p>
          {item.evidencia ? (
            <p className="nums mt-1 text-xs text-muted-foreground">Base: {item.evidencia}</p>
          ) : null}
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-3 border-t border-border pt-3">
        <Link
          to={hrefDoDestino(workspace.slug, item.acao.destino)}
          className="inline-flex min-h-11 items-center gap-1 text-sm font-medium text-foreground hover:text-primary-text md:min-h-0"
        >
          {item.acao.rotulo}
          <ArrowRight aria-hidden="true" className="size-4" />
        </Link>
        {podeDispensar ? (
          perguntando ? (
            <span className="flex flex-wrap items-center justify-end gap-1">
              {MOTIVOS.map((m) => (
                <Button
                  key={m.rotulo}
                  type="button"
                  variant="ghost"
                  size="sm"
                  loading={dismiss.isPending && escolhido === m.motivo}
                  disabled={dismiss.isPending}
                  onClick={() => {
                    setEscolhido(m.motivo);
                    dispensar(m.motivo);
                  }}
                >
                  {m.rotulo}
                </Button>
              ))}
            </span>
          ) : (
            <Button type="button" variant="ghost" size="sm" onClick={() => setPerguntando(true)}>
              Dispensar
            </Button>
          )
        ) : null}
      </div>
    </Card>
  );
}
